import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DestroyRef,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    ViewChild,
    inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { OptionGroupType, OptionGroupTypeService } from '@frontend/sports/betting-offer/feature/fixture-factories';
import { CrmPromoIdsMap, EventModel, EventOptionGroup, EventOptionGroupType } from '@frontend/sports/betting-offer/feature/model';
import { ScrollAdapterButtonSize } from '@frontend/sports/common/core/feature/scroll-adapter';
import { ISimpleChanges } from '@frontend/sports/common/core/utils/simple-change';
import { SourceOfPick } from '@frontend/sports/option/pick/feature/tracking';
import { OptionPanelThreshold } from '@frontend/sports/types/configuration';
import { TabBarEvent, TabBarItem } from '@frontend/sports/web/tab-bar-utils';
import { DsDivider } from '@frontend/ui/divider';
import { DsTabsModule } from '@frontend/ui/tabsgroup';
import { Subscription } from 'rxjs';

import { SHEET_VIEW_OPEN } from '../sheet-view-core/sheet-view.token';
import { TabControllerFacadeService } from '../sheet-view-core/store/tab-controller-facade.service';
import { TabControllerResponseState } from '../sheet-view-core/store/tab-controller.model';
import { BetBuilderCorrectScoreOptionGroupComponent } from './bet-builder-correct-score-option-group.component';
import { BetBuilderPlayersOptionGroupComponent } from './bet-builder-goalscorer-option-group.component';
import { BetBuilderOverUnderOptionGroupComponent } from './bet-builder-over-under-option-group.component';
import { BetBuilderSixPackComponent } from './bet-builder-six-pack.component';
import { BetBuilderSpreadOptionGroupComponent } from './bet-builder-spread-option-group.component';
import { BetBuilderStandardGroupComponent } from './bet-builder-standard-group.component';
import { BetBuilderPlayerTeamOptionGroupComponent } from './bet-builder-teams-option-group.component';
import { CorrectScoreOptionGroupWrapperComponent } from './correct-score-option-group-wrapper.component';
import { GoalScorerGroupComponent } from './goal-scorer-group.component';
import { HeadToHeadOptionGroupComponent } from './head-to-head-option-group.component';
import { OptionGroupService } from './option-group.service';
import { OverUnderOptionGroupComponent } from './over-under-option-group.component';
import { PlayerMilestoneOptionGroupComponent } from './player-milestone-option-group.component';
import { PlayerMilestoneOverUnderOptionGroupComponent } from './player-milestone-over-under-option-group.component';
import { PlayerPropsOptionGroupComponent } from './player-props-option-group.component';
import { RegularOptionGroupComponent } from './regular-option-group.component';
import { ScoreMarketOptionGroupComponent } from './score-market-option-group.component';
import { ScorecastOptionGroupComponent } from './scorecast/scorecast-option-group.component';
import { WincastOptionGroupComponent } from './scorecast/wincast/wincast-option-group.component';
import { SixPackOptionGroupComponent } from './six-pack-option-group.component';
import { SplitHeaderComponent } from './split-header/split-header.component';
import { SpreadOptionGroupComponent } from './spread-option-group.component';
import { TotalScoreOptionGroupComponent } from './total-score-option-group.component';
import { YesNoOptionGroupComponent } from './yes-no-option-group.component';

@Component({
    selector: 'ms-period-option-group',
    templateUrl: './period-option-group.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        RegularOptionGroupComponent,
        CorrectScoreOptionGroupWrapperComponent,
        PlayerMilestoneOptionGroupComponent,
        PlayerMilestoneOverUnderOptionGroupComponent,
        OverUnderOptionGroupComponent,
        GoalScorerGroupComponent,
        SixPackOptionGroupComponent,
        PlayerPropsOptionGroupComponent,
        SpreadOptionGroupComponent,
        ScorecastOptionGroupComponent,
        WincastOptionGroupComponent,
        ScoreMarketOptionGroupComponent,
        YesNoOptionGroupComponent,
        TotalScoreOptionGroupComponent,
        HeadToHeadOptionGroupComponent,
        BetBuilderStandardGroupComponent,
        BetBuilderSixPackComponent,
        BetBuilderOverUnderOptionGroupComponent,
        BetBuilderCorrectScoreOptionGroupComponent,
        BetBuilderSpreadOptionGroupComponent,
        BetBuilderPlayerTeamOptionGroupComponent,
        BetBuilderPlayersOptionGroupComponent,
        SplitHeaderComponent,
        DsTabsModule,
        DsDivider,
    ],
})
export class PeriodOptionGroupComponent implements OnChanges {
    private helper = inject(OptionGroupService);
    private typeHelper = inject(OptionGroupTypeService);
    private changeDetector = inject(ChangeDetectorRef);

    @Input() tracking: string;
    @Input() trackingModuleType = '';
    @Input() event: EventModel;
    @Input() optionGroup: EventOptionGroup;
    @Input() thresholds?: OptionPanelThreshold[];
    @Input() showMarketHelp: boolean;
    @Input() crmPromoIdsMap: CrmPromoIdsMap;
    @Input() scrollAdapterButtonSize: ScrollAdapterButtonSize;
    @Input() sourceOfPick = SourceOfPick.Other;
    @Input() betDetails = '';
    @Input() optionsLimitOverride?: number;
    @Input() showSixPackLogosColor: boolean;
    @Input() showPlayerStats = false;
    @Input() showSixPackLogos: boolean;

    @Output() periodChange = new EventEmitter<number>();
    @Output() activePeriodChange = new EventEmitter<TabBarEvent>();
    @Output() showAllRequested = new EventEmitter<void>();

    @ViewChild('splitHeaderComponent') splitHeaderComponent: SplitHeaderComponent;

    readonly isInSheetView = Boolean(inject(SHEET_VIEW_OPEN, { optional: true }));

    more = false;

    activeGroup: EventOptionGroup;
    periodTabs: TabBarItem[];
    groupTypes = OptionGroupType;
    groupType: OptionGroupType;

    marketGroupId: string;
    tabControllerState: TabControllerResponseState | null;
    tabIndex: { lastActiveTabId: string; lastActiveTabIndex: number };
    tabControllerStateChangeSubscription: Subscription;
    private readonly destroyRef = inject(DestroyRef);
    private tabControllerFacadeService = inject(TabControllerFacadeService);

    ngOnChanges(changes: ISimpleChanges<PeriodOptionGroupComponent>): void {
        this.marketGroupId = this.optionGroup.detailedGrouping.marketGroupId;
        const group = changes.optionGroup;

        if (group) {
            this.handleTabControllerStateChange();

            if (!group.previousValue || group.previousValue.id !== group.currentValue.id) {
                this.groupType = this.activeGroup ? this.typeHelper.getOptionGroupType(this.activeGroup) : OptionGroupType.Period;
            }

            this.periodTabs?.forEach((tab) => {
                if (
                    tab.attribute &&
                    (this.groupType === this.groupTypes.Regular || this.groupType === this.groupTypes.CorrectScore) &&
                    this.crmPromoIdsMap.markets[tab.attribute]
                ) {
                    tab.badges = [
                        {
                            class: 'promotion-simple-badge badge badge-info',
                            icon: 'icon theme-badge',
                        },
                    ];
                }
            });
        }
    }

    handleTabControllerStateChange() {
        this.tabControllerStateChangeSubscription?.unsubscribe();
        this.tabControllerStateChangeSubscription = this.tabControllerFacadeService
            .getTabControllerState(this.optionGroup.detailedGrouping.marketGroupId, this.event.id)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((data: TabControllerResponseState | null) => {
                this.tabControllerState = data;
                this.handlePeriodTabs();
                this.changeDetector.markForCheck();
            });
    }

    setPeriodTabSelected(index: string): void {
        const selectedTab = this.periodTabs[Number(index)];
        this.activePeriodChange.emit({ active: selectedTab! });
        this.periodChange.emit(selectedTab.id);
        this.setTabActivate(selectedTab.id);
        this.splitHeaderComponent?.scrollToTop();
    }

    get activeIndex(): string {
        return this.periodTabs?.findIndex((t) => t.active).toString() ?? '-1';
    }

    private setTabActivate(activeTabId: number): void {
        this.periodTabs.forEach((tab) => (tab.active = tab.id === activeTabId));
        this.periodTabs = [...this.periodTabs];
        this.setOptionsActive(activeTabId);
    }

    private handlePeriodTabs(): void {
        this.tabIndex = {
            lastActiveTabId: this.marketGroupId,
            lastActiveTabIndex: this.tabControllerState ? this.tabControllerState.lastSelectedTabIndex : -1,
        };

        this.periodTabs = this.helper.getPeriodTabs(this.optionGroup, this.tabIndex);

        if (this.periodTabs && this.optionGroup.hasSubGroups(EventOptionGroupType.Period)) {
            const activeTab = this.periodTabs.find((p) => p.active === true);
            if (activeTab) {
                this.setOptionsActive(activeTab.id);
            }
        }
    }

    private setOptionsActive(active: number): void {
        const tabIndex = this.periodTabs.findIndex((tab) => tab.id === active);
        const payload = { marketGroupId: this.marketGroupId, tabIndex, eventId: this.event.id };
        if (this.tabControllerState) {
            const tabIndexPresent = tabIndex in this.tabControllerState;
            if (!tabIndexPresent || tabIndex !== this.tabControllerState.lastSelectedTabIndex) {
                this.tabControllerFacadeService.createOrUpdateTab(payload);
            }
        } else {
            this.tabControllerFacadeService.createOrUpdateTab(payload);
        }
        if (this.optionGroup.hasSubGroups(EventOptionGroupType.Period)) {
            const activeGroup = this.optionGroup
                .getSubGroups(EventOptionGroupType.Period)
                .find((p) => p.detailedGrouping && p.detailedGrouping.periodGroup === active);
            if (activeGroup) {
                this.activeGroup = activeGroup;
            }
        }
    }
}
